/*******************************************************************************
  ScheduleItemTable.js

  Author: Advait

*******************************************************************************/
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { API, graphqlOperation } from "aws-amplify";
import { updateScheduleItem } from "../graphql/mutations";
import { showNotification } from "../components/helper/notification";
import "../index.css";

const hours = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];
const hour = hours.map((hours) => (
  <option key={hours} value={hours}>
    {hours}
  </option>
));

const duration_hours = ["0", "1", "2", "3", "4", "5", "6", "7", "8"];
const duration = duration_hours.map((duration_hours) => (
  <option key={duration_hours} value={duration_hours}>
    {duration_hours}
  </option>
));

const minutes = ["00", "15", "30", "45"];
const minute = minutes.map((minutes) => (
  <option key={minutes} value={minutes}>
    {minutes}
  </option>
));

const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const graphqlLimit = 500;

const listSchedules = `query ListScheduleItems(
  $filter: ModelScheduleItemFilterInput
  $limit: Int
  $nextToken: String
) {
  listScheduleItems(filter: $filter, limit: $limit, nextToken: $nextToken) {
    items {
      id
      siteId
      enabled
      type
      day
      startTime
      duration
    }
    nextToken
  }
}
`;

function ScheduleItemTable({ scheduleType }) {
  const selectedSiteId = useSelector((state) => state.selectedSite.siteId);
  const [siteSchedule, setSiteSchedule] = useState([]);
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);
  useEffect(() => {
    //***************************************************************************
    // Update the UI and display the content as per latest states.
    //***************************************************************************
    function handleChangeState(allschedules) {
      //***************************************************************************
      // Convert the 24 hr time format string values into 12 hr time format
      //***************************************************************************
      for (let i in allschedules) {
        let day = allschedules[i].day;

        let startTime = allschedules[i].startTime.split(":");
        // All numeric values are stored as strings in allschedules items.
        // Let javascript auto convert string to number for numeric operations.
        allschedules[i].startMin = startTime[1];
        if (startTime[0] === "00") {
          allschedules[i].startHr = "12";
          allschedules[i].startAM = "AM";
        } else if (startTime[0] < 12) {
          allschedules[i].startHr = startTime[0];
          allschedules[i].startAM = "AM";
        } else if (startTime[0] === "12") {
          allschedules[i].startHr = "12";
          allschedules[i].startAM = "PM";
        } else {
          allschedules[i].startHr = (startTime[0] - 12).toString();
          allschedules[i].startAM = "PM";
        }
        if (allschedules[i].duration) {
          let durationTime = allschedules[i].duration.split(":");
          allschedules[i].durationHr = durationTime[0];
          allschedules[i].durationMin = durationTime[1];
        }
        allschedules[i].dayName = days[day];
      }

      //sort based on day number
      allschedules.sort(function (a, b) {
        return a.day.localeCompare(b.day);
      });

      setSiteSchedule(allschedules);
      setSaveButtonDisabled(true);
    }

    /**
     * queries the Schedule dynamodb table and then handles the state change
     */
    async function updateSchedules() {
      // get the schedule items from grphql query
      // filter based on type and siteId
      let userRequestedItemType = scheduleType;
      let userRequestedSite = selectedSiteId;
      let siteAndTypeFilter = {
        siteId: {
          eq: userRequestedSite,
        },
        type: {
          eq: userRequestedItemType,
        },
      };
      let list_schedules = await API.graphql(
        graphqlOperation(listSchedules, {
          filter: siteAndTypeFilter,
          limit: graphqlLimit,
        })
      );
      let allschedules = list_schedules.data.listScheduleItems.items;
      //***************************************************************************
      // If the token is null that means there is no more data in the database,
      // but if the there is token then following code will run until we recieve the
      // null token.
      //***************************************************************************
      let token = list_schedules.data.listScheduleItems.nextToken;
      while (token !== null) {
        let newSchedules = {};
        newSchedules = await API.graphql(
          graphqlOperation(listSchedules, {
            filter: siteAndTypeFilter,
            limit: graphqlLimit,
            nextToken: token,
          })
        );
        token = newSchedules.data.listScheduleItems.nextToken;
        allschedules = allschedules.concat(newSchedules.data.listScheduleItems.items);
      }

      handleChangeState(allschedules);
    }

    if (selectedSiteId) updateSchedules();
  }, [scheduleType, selectedSiteId]);

  //***************************************************************************
  // update the schedule item if user changes any value from the table
  //***************************************************************************
  function handleUpdateScheduleValues(e, i, variable, UpdatedSchedule) {
    let currentSchedule = siteSchedule;
    //let UpdatedSchedule = siteSchedule

    // Update parameters which are changed by user
    if (variable === "startHr") {
      UpdatedSchedule.startHr = e.target.value;
    } else if (variable === "startMin") {
      UpdatedSchedule.startMin = e.target.value;
    } else if (variable === "startAM") {
      UpdatedSchedule.startAM = e.target.value;
    } else if (variable === "durationHr") {
      UpdatedSchedule.durationHr = e.target.value;
    } else if (variable === "durationMin") {
      UpdatedSchedule.durationMin = e.target.value;
    } else if (variable === "enabled") {
      UpdatedSchedule.enabled = !UpdatedSchedule.enabled;
    }

    //convert the updated time into 24 hr string format
    //NOTE: If need to change the time format as per the user location then chnage the code here
    if (UpdatedSchedule.startAM === "AM") {
      if (UpdatedSchedule.startHr === "12") {
        UpdatedSchedule.startTime = "00:" + UpdatedSchedule.startMin;
      } else {
        UpdatedSchedule.startTime = UpdatedSchedule.startHr + ":" + UpdatedSchedule.startMin;
      }
    } else {
      if (UpdatedSchedule.startHr === "12") {
        UpdatedSchedule.startTime = "12:" + UpdatedSchedule.startMin;
      } else {
        UpdatedSchedule.startTime =
          parseInt(UpdatedSchedule.startHr) + 12 + ":" + UpdatedSchedule.startMin;
      }
    }

    let duration = UpdatedSchedule.durationHr + ":" + UpdatedSchedule.durationMin;

    UpdatedSchedule.duration = duration;

    currentSchedule[i] = UpdatedSchedule;
    setSiteSchedule([].concat(currentSchedule));
    setSaveButtonDisabled(false);
  }

  //***************************************************************************
  // Call update mutation on save button
  //***************************************************************************
  async function handleUpdateSchedule(e) {
    setSaveButtonDisabled(true);
    const UpdatedSchedule = siteSchedule;
    console.log("UpdatedSchedule", UpdatedSchedule);
    let schedule = {};
    let errorinUpdatingSchedule = false;
    for (let i in UpdatedSchedule) {
      schedule = {
        id: UpdatedSchedule[i].id,
        enabled: UpdatedSchedule[i].enabled,
        startTime: UpdatedSchedule[i].startTime,
        duration: UpdatedSchedule[i].duration,
      };
      console.log("schedule", schedule);
      try {
        const response = await API.graphql(
          graphqlOperation(updateScheduleItem, { input: schedule })
        );
        console.log(response);
        if (response) {
          console.log("Schedule updated successfully.");
        } else {
          errorinUpdatingSchedule = true;
        }
      } catch (e) {
        console.log(e);
        errorinUpdatingSchedule = true;
      }
    }
    //pass a string to potray positive response
    showNotification(errorinUpdatingSchedule ? null : "success");
    try {
      // tell task manager we just updated the schedule
      await API.post("fataskman", "/sites/" + selectedSiteId, {
        body: { action: "update_schedule" },
      });
    } catch (e) {
      console.log(e);
    }
  }

  //***************************************************************************
  // render the UI
  //***************************************************************************
  const scheduleData = [].concat(siteSchedule).map((siteSchedule, i) => (
    <tr key={i}>
      <td style={{ width: "99.75px", paddingRight: "15px" }}>{siteSchedule.dayName}</td>
      <td style={{ paddingRight: "15px" }}>
        <input
          onChange={(e) => handleUpdateScheduleValues(e, i, "enabled", siteSchedule)}
          name="completed"
          type="checkbox"
          checked={siteSchedule.enabled}
        />
      </td>
      <td style={{ paddingRight: "15px" }}>
        <select
          value={siteSchedule.startHr}
          onChange={(e) => handleUpdateScheduleValues(e, i, "startHr", siteSchedule)}
        >
          {hour}
        </select>

        <select
          value={siteSchedule.startMin}
          onChange={(e) => handleUpdateScheduleValues(e, i, "startMin", siteSchedule)}
        >
          {minute}
        </select>

        <select
          value={siteSchedule.startAM}
          onChange={(e) => handleUpdateScheduleValues(e, i, "startAM", siteSchedule)}
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </td>
      {scheduleType === "reception" || scheduleType === "security_patrol" ? (
        <td style={{ paddingRight: "15px" }}>
          <select
            value={siteSchedule.durationHr}
            onChange={(e) => handleUpdateScheduleValues(e, i, "durationHr", siteSchedule)}
          >
            {duration}
          </select>
          <select
            value={siteSchedule.durationMin}
            onChange={(e) => handleUpdateScheduleValues(e, i, "durationMin", siteSchedule)}
          >
            {minute}
          </select>
        </td>
      ) : null}
    </tr>
  ));
  return (
    <div className="individualContainer">
      <table className="individualContainerTable">
        <tbody>
          <tr>
            <td className="individualContainerTitleColumn" valign="top">
              <h4 className="individualContainerTitleHeading">Schedule</h4>
            </td>
            <td>
              <table>
                <thead>
                  <tr>
                    <th>Day</th>
                    <th>Enabled</th>
                    <th>Start Time</th>
                    {scheduleType === "reception" || scheduleType === "security_patrol" ? (
                      <th>Duration</th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>{scheduleData}</tbody>
              </table>
            </td>
          </tr>
          <tr>
            <td className="individualContainerTitleColumn" valign="top"></td>
            <td style={{ padding: "0 0 15px 0" }}>
              <button
                className="saveButton"
                onClick={handleUpdateSchedule}
                disabled={saveButtonDisabled}
              >
                Save
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
export default ScheduleItemTable;
